<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { API, HOST } from '@/utils/http'
import { useRoute } from 'vue-router'
import ProjectFilters from './ProjectFilters.vue'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Save, Download } from 'lucide-vue-next'
import LayerFeaturesList from '@/components/layers/variants/LayerFeaturesList.vue'
import AIReporting from '@/components/layers/AIReporting.vue'

const props = defineProps({
  layer: {
    type: Object,
    required: true
  },
  activeTab: {
    type: String,
    required: true
  }
})

const route = useRoute()
const store = useStore()
const orgId = computed(() => route.params.org_id)
const layerId = computed(() => route.params.layer_id)
const isUpdating = ref(false)
const isDownloading = ref(false)
const currentFilters = ref(null)

const layerCode = computed(() => {
  return props.layer?.uuid
})

const downloadCSV = async () => {
  if (!layerCode.value) return

  isDownloading.value = true
  try {
    // Get current date range from the store
    const selectedRange = store.getters['layers/getSelectedRange']

    let url = `${HOST}/org/${orgId.value}/layers/${layerCode.value}/features/csv`
    const params = {}

    if (selectedRange && Array.isArray(selectedRange) && selectedRange.length >= 2) {
      params.startDate = selectedRange[0]
      params.endDate = selectedRange[1]
    }

    const response = await API({
      method: 'GET',
      url: url,
      params: params,
      responseType: 'blob' // Important for file downloads
    })

    // Get filename from Content-Disposition header or create default
    let filename = 'features_export.csv'
    if (response.headers && response.headers['content-disposition']) {
      const contentDisposition = response.headers['content-disposition']
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
      if (filenameMatch) {
        filename = filenameMatch[1]
      }
    }

    // If no filename in headers, create one with timestamp
    if (filename === 'features_export.csv') {
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_')
      filename = `features_export_${timestamp}.csv`
    }

    // Create blob and download
    const blob = new Blob([response.data], { type: 'text/csv' })
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(downloadUrl)

    toast({
      title: 'Download successful',
      description: `CSV file ${filename} downloaded successfully`,
      variant: 'default'
    })

  } catch (error) {
    console.error('Error downloading CSV:', error)
    toast({
      title: 'Download failed',
      description: 'Failed to download CSV file',
      variant: 'destructive'
    })
  } finally {
    isDownloading.value = false
  }
}



const saveFilterChanges = async (filterData) => {
  if (!props.layer || props.layer.properties.type !== 4) return

  isUpdating.value = true
  try {
    const filterConfig = {
      type: "AndFilter",
      config: []
    };

    if (filterData.projectIds && filterData.projectIds.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "project_id",
        config: filterData.projectIds.map(id => id.toString())
      });
    }

    if (filterData.templateIds && filterData.templateIds.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "template_id",
        config: filterData.templateIds.map(id => id.toString())
      });
    }

    if (filterData.memberIds && filterData.memberIds.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "member_id",
        config: filterData.memberIds.map(id => id.toString())
      });
    }

    if (filterData.searchText) {
      filterConfig.config.push({
        type: "TextFilter",
        field_name: "search",
        config: filterData.searchText
      });
    }

    if (filterData.isPublicData !== undefined) {
      filterConfig.config.push({
        type: "BoolFilter",
        field_name: "is_public",
        config: filterData.isPublicData
      });
    }

    if (filterData.dateRange && filterData.dateRange.start && filterData.dateRange.end) {
      filterConfig.config.push({
        type: "DateRangeFilter",
        field_name: "acquired",
        config: {
          gte: filterData.dateRange.start.toISOString(),
          lte: filterData.dateRange.end.toISOString()
        }
      });
    }

    if (filterData.geometry) {
      filterConfig.config.push({
        type: "GeometryFilter",
        field_name: "geometry",
        config: filterData.geometry.geometry
      });
    }

    if (filterData.geometryTypes && filterData.geometryTypes.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "geometry_type",
        config: filterData.geometryTypes
      });
    }

    await API({
      method: 'PATCH',
      url: `${HOST}/org/${orgId.value}/layers/${layerId.value}/collected-data`,
      data: {
        filter: filterConfig
      }
    })

  } catch (error) {
    console.error('Error saving filters:', error)
    toast({
      title: 'Update failed',
      description: 'Failed to save filter settings',
      variant: 'destructive'
    });
  } finally {
    isUpdating.value = false;
  }
}

const updateFilters = (filters) => {
  currentFilters.value = filters
}
</script>

<template>
  <div>
    <!-- Data Tab Content -->
    <div v-if="activeTab === 'data'">
      <!-- Download CSV Button -->
      <div class="flex justify-end items-center gap-3 mb-4">
        <Button
          @click="downloadCSV"
          :disabled="isDownloading"
          variant="outline"
          size="sm"
          class="border-primary text-primary hover:bg-primary hover:text-white"
        >
          <Download class="h-4 w-4 mr-2" />
          <span v-if="isDownloading">...</span>
          <span v-else>Download CSV</span>
        </Button>
      </div>

      <LayerFeaturesList :code="layerCode" />
    </div>

    <!-- AI Reports Tab Content -->
    <div v-if="activeTab === 'ai-reports'">
      <AIReporting :layer="layer" />
    </div>

    <!-- Filter Tab Content -->
    <div v-if="activeTab === 'filter'">
      <div class="flex justify-between items-center mb-4">
        <Button
          @click="saveFilterChanges(currentFilters)"
          :disabled="isUpdating"
          size="sm"
          class="bg-primary text-white hover:bg-primary/90"
        >
          <Save class="h-4 w-4 mr-2" />
          <span v-if="isUpdating">Saving...</span>
          <span v-else>Save</span>
        </Button>
      </div>

      <ProjectFilters
        v-if="layer?.properties?.layer?.data"
        :initial-filter="layer?.properties?.layer?.data"
        @update:filters="updateFilters"
      />
    </div>
  </div>
</template>
